import {isResultPresentationEnabled, observeResultPresentation} from '@github-ui/ahp-relay/relay-result-presentation'
import type {EstablishedAhpSessionIdentity} from '@github-ui/ahp-session-client/contracts'
import type {UiReadinessSurface} from './ui-readiness-contract'
import {createContext, use, useCallback, useEffect, useMemo, useSyncExternalStore} from 'react'

import {
  type CopilotResultControls,
  type CopilotResultPresentationSelection,
  type CopilotResultPresentationView,
  type CopilotResultRenderIdentity,
  createResultPresentationSelection,
} from './copilot-result-presentation'
import {CopilotResultPresentationRootContext} from './copilot-result-presentation-root'
import {useOneCopilotBrowserTelemetryEnabled} from './use-one-copilot-browser-telemetry-enabled'

export const CopilotResultPresentationContext = createContext<CopilotResultPresentationView | undefined>(undefined)

export function useCopilotResultPresentationSelection(
  identity: EstablishedAhpSessionIdentity | undefined,
  surface: UiReadinessSurface,
) {
  const enabled = useOneCopilotBrowserTelemetryEnabled(isResultPresentationEnabled())
  const root = use(CopilotResultPresentationRootContext)
  const selection = useMemo(
    () => (enabled ? createResultPresentationSelection(surface) : undefined),
    [enabled, surface],
  )
  const {taskId, sessionId, environmentId, channel} = identity ?? {}
  useEffect(() => selection?.mount(), [selection])
  useEffect(() => {
    const selected = taskId && sessionId && environmentId ? {taskId, sessionId, environmentId, channel} : undefined
    if (selected && selection) {
      if (root && !root.matches(selected)) {
        selection.select(undefined)
        return
      }
      const release = root?.claim(selection, selected)
      if (release) return release
    }
    selection?.select(selected, false)
  }, [selection, root, taskId, sessionId, environmentId, channel])
  return selection
}

const subscribeNothing = () => () => undefined
const emptySnapshot = () => undefined

export function useCopilotResultPresentationView(selection: CopilotResultPresentationSelection | undefined) {
  return useSyncExternalStore(
    selection?.subscribe ?? subscribeNothing,
    selection?.getSnapshot ?? emptySnapshot,
    emptySnapshot,
  )
}

export function useCopilotResultElement(
  identity: CopilotResultRenderIdentity,
  kind: 'content' | 'terminal',
  eligible = true,
) {
  const view = use(CopilotResultPresentationContext)
  const {task_id, session_id, environment_id, chat_id, turn_id, part_id, terminal_state} = identity
  return useCallback(
    (element: HTMLElement | null) => {
      if (!view || !element || !eligible) return
      return observeResultPresentation(() =>
        view.register(element, {task_id, session_id, environment_id, chat_id, turn_id, part_id, terminal_state}, kind),
      )
    },
    [view, eligible, task_id, session_id, environment_id, chat_id, turn_id, part_id, terminal_state, kind],
  )
}

export function useCopilotResultControls(hidden = false) {
  const view = use(CopilotResultPresentationContext)
  const observe = useCallback(
    (controls: CopilotResultControls) => observeResultPresentation(() => view?.controls(controls)),
    [view],
  )
  return view && !hidden ? observe : undefined
}
